import React, { useEffect, useState, useContext, useRef } from 'react';
import authenticate from '../../utils/Authenticate';
import UserContext from '../../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const EditDishModal = ({ isOpen, onClose, dish, onSave }) => {
  const [editData, setEditData] = useState({
    name: '',
    quantity: '',
    unit: ''
  });
  const [ingredients, setIngredients] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [ingredientSearchResults, setIngredientSearchResults] = useState({});
  const [ingredientSearchLoading, setIngredientSearchLoading] = useState({});
  const [notification, setNotification] = useState({ message: '', type: '' });
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  
  // New states for keyboard navigation
  const [selectedDishIndex, setSelectedDishIndex] = useState(-1);
  const [selectedIngredientIndex, setSelectedIngredientIndex] = useState({});
  const [activeDishSearch, setActiveDishSearch] = useState(false);
  const [activeIngredientSearch, setActiveIngredientSearch] = useState({});
  const { user,setUser } = useContext(UserContext);

  useEffect( () => {
    async function startup(){
      if(!user){
        await authenticate(setUser,navigate);
      }
    }
    startup();
  },[user])

  
  // Refs for auto-scroll
  const notificationRef = useRef(null);
  const modalContentRef = useRef(null);

  // Initialize form data when dish prop changes
  useEffect(() => {
    if (dish && isOpen) {
      setEditData({
        name: dish.name || '',
        quantity: dish.quantity || '',
        unit: dish.unit || ''
      });
      
      // Initialize ingredients
      const initialIngredients = dish.ingredients?.map((ing, index) => ({
        id: index,
        ingredientId: ing.name?._id || '',
        name: ing.name?.name || '',
        quantity: ing.quantity || '',
        unit: ing.name?.unit || '',
        searchTerm: ing.name?.name || '',
        selectedIngredient: ing.name || null
      })) || [];
      
      setIngredients(initialIngredients);
    }
  }, [dish, isOpen]);

  // Auto-scroll to notification when it appears
  useEffect(() => {
    if (notification.message && notificationRef.current) {
      notificationRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  }, [notification]);

  // Search dishes for main dish name
  const handleDishSearch = async (searchValue) => {
    setEditData(prev => ({ ...prev, name: searchValue }));
    setActiveDishSearch(true);
    setSelectedDishIndex(-1);
    
    if (searchValue.length < 2) {
      setSearchResults([]);
      setActiveDishSearch(false);
      return;
    }

    try {
      setSearchLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/dish/search?q=${searchValue}`,
        { withCredentials: true }
      );
      setSearchResults(response.data.data || []);
    } catch (error) {
      console.error('Error searching dishes:', error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Handle keyboard navigation for dish search
  const handleDishKeyDown = (e) => {
    if (!activeDishSearch || searchResults.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedDishIndex(prev => 
          prev < searchResults.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedDishIndex(prev => 
          prev > 0 ? prev - 1 : searchResults.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedDishIndex >= 0) {
          handleSelectDish(searchResults[selectedDishIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setSearchResults([]);
        setActiveDishSearch(false);
        setSelectedDishIndex(-1);
        break;
    }
  };

  // Select dish from search results
  const handleSelectDish = (selectedDish) => {
    setEditData({
      name: selectedDish.name,
      quantity: editData.quantity,
      unit: selectedDish.unit
    });
    setSearchResults([]);
    setActiveDishSearch(false);
    setSelectedDishIndex(-1);
  };

  // Search ingredients
  const handleIngredientSearch = async (ingredientIndex, searchValue) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[ingredientIndex].searchTerm = searchValue;
    updatedIngredients[ingredientIndex].name = searchValue;
    setIngredients(updatedIngredients);
    
    setActiveIngredientSearch(prev => ({ ...prev, [ingredientIndex]: true }));
    setSelectedIngredientIndex(prev => ({ ...prev, [ingredientIndex]: -1 }));

    try {
      setIngredientSearchLoading(prev => ({ ...prev, [ingredientIndex]: true }));
      const response = await axios.get(
        `${process.env.REACT_APP_API_URI}/api/findItems`,
        {
          params: { q: searchValue },
        },
        { withCredentials: true }
      );
      setIngredientSearchResults(prev => ({
        ...prev,
        [ingredientIndex]: response.data || []
      }));
    } catch (error) {
      console.error('Error searching ingredients:', error);
    } finally {
      setIngredientSearchLoading(prev => ({ ...prev, [ingredientIndex]: false }));
    }
  };

  // Handle keyboard navigation for ingredient search
  const handleIngredientKeyDown = (e, ingredientIndex) => {
    const isActive = activeIngredientSearch[ingredientIndex];
    const results = ingredientSearchResults[ingredientIndex] || [];
    
    if (!isActive || results.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIngredientIndex(prev => ({
          ...prev,
          [ingredientIndex]: prev[ingredientIndex] < results.length - 1 
            ? prev[ingredientIndex] + 1 
            : 0
        }));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIngredientIndex(prev => ({
          ...prev,
          [ingredientIndex]: prev[ingredientIndex] > 0 
            ? prev[ingredientIndex] - 1 
            : results.length - 1
        }));
        break;
      case 'Enter':
        e.preventDefault();
        const currentIndex = selectedIngredientIndex[ingredientIndex];
        if (currentIndex >= 0) {
          handleSelectIngredient(ingredientIndex, results[currentIndex]);
        }
        break;
      case 'Escape':
        e.preventDefault();
        setIngredientSearchResults(prev => ({ ...prev, [ingredientIndex]: [] }));
        setActiveIngredientSearch(prev => ({ ...prev, [ingredientIndex]: false }));
        setSelectedIngredientIndex(prev => ({ ...prev, [ingredientIndex]: -1 }));
        break;
    }
  };

  // Select ingredient from search results
  const handleSelectIngredient = (ingredientIndex, selectedIngredient) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[ingredientIndex] = {
      ...updatedIngredients[ingredientIndex],
      ingredientId: selectedIngredient._id,
      name: selectedIngredient.name,
      unit: selectedIngredient.unit,
      searchTerm: selectedIngredient.name,
      selectedIngredient: selectedIngredient
    };
    setIngredients(updatedIngredients);
    
    // Clear search results for this ingredient
    setIngredientSearchResults(prev => ({ ...prev, [ingredientIndex]: [] }));
    setActiveIngredientSearch(prev => ({ ...prev, [ingredientIndex]: false }));
    setSelectedIngredientIndex(prev => ({ ...prev, [ingredientIndex]: -1 }));
  };

  // Handle ingredient quantity change
  const handleIngredientQuantityChange = (ingredientIndex, quantity) => {
    const updatedIngredients = [...ingredients];
    updatedIngredients[ingredientIndex].quantity = quantity;
    setIngredients(updatedIngredients);
  };

  // Add new ingredient
  const handleAddIngredient = () => {
    const newIngredient = {
      id: ingredients.length,
      ingredientId: '',
      name: '',
      quantity: '',
      unit: '',
      searchTerm: '',
      selectedIngredient: null
    };
    setIngredients([...ingredients, newIngredient]);
  };

  // Remove ingredient
  const handleRemoveIngredient = (ingredientIndex) => {
    if (ingredients.length > 1) {
      const updatedIngredients = ingredients.filter((_, index) => index !== ingredientIndex);
      setIngredients(updatedIngredients);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!editData.name || !editData.quantity) {
      setNotification({ message: 'Please fill in dish name and quantity', type: 'error' });
      return;
    }

    const invalidIngredients = ingredients.some(ing => 
      !ing.ingredientId || !ing.quantity || parseFloat(ing.quantity) <= 0
    );

    if (invalidIngredients) {
      setNotification({ message: 'Please complete all ingredient details', type: 'error' });
      return;
    }

    try {
      setSubmitting(true);
      
      const updateData = {
        dishId: dish._id,
        name: editData.name,
        quantity: parseFloat(editData.quantity),
        unit: editData.unit,
        ingredients: ingredients.map(ing => ({
          ingredientId: ing.ingredientId,
          quantity: parseFloat(ing.quantity)
        }))
      };

      const response = await axios.post(
        `${process.env.REACT_APP_API_URI}/api/dish/edit`,
        updateData,
        { withCredentials: true }
      );

      setNotification({ message: 'Dish updated successfully!', type: 'success' });
      
      // Call onSave callback to refresh parent component
      if (onSave) {
        onSave(response.data.data);
      }
      
      setTimeout(() => {
        onClose();
        setNotification({ message: '', type: '' });
      }, 1500);

    } catch (error) {
      console.error('Error updating dish:', error);
      setNotification({ 
        message: error.response?.data?.message || 'Failed to update dish', 
        type: 'error' 
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    setNotification({ message: '', type: '' });
    setSearchResults([]);
    setIngredientSearchResults({});
    setActiveIngredientSearch({});
    setActiveDishSearch(false);
    setSelectedDishIndex(-1);
    setSelectedIngredientIndex({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto" ref={modalContentRef}>
        {/* Modal Header */}
        <div className="bg-lime-800 text-white p-4 flex justify-between items-center rounded-t-lg sticky top-0 z-10">
          <h2 className="text-xl font-semibold">Edit Dish</h2>
          <button
            onClick={handleClose}
            className="text-white hover:text-gray-200 text-2xl"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Notification */}
          {notification.message && (
            <div 
              ref={notificationRef}
              className={`p-3 mb-4 rounded ${
                notification.type === 'error' 
                  ? 'bg-red-100 text-red-700 border border-red-300' 
                  : 'bg-green-100 text-green-700 border border-green-300'
              }`}
            >
              {notification.message}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Dish Details Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Dish Details
              </h3>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Dish Name Search */}
                <div className="relative">
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Dish Name *
                  </label>
                  <input
                    type="text"
                    disabled
                    value={editData.name}
                    onChange={(e) => handleDishSearch(e.target.value)}
                    onKeyDown={handleDishKeyDown}
                    onFocus={() => setActiveDishSearch(true)}
                    onBlur={() => {
                      // Delay to allow click on dropdown items
                      setTimeout(() => {
                        setActiveDishSearch(false);
                        setSelectedDishIndex(-1);
                      }, 200);
                    }}
                    placeholder="Search and select dish..."
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                  {searchLoading && (
                    <p className="text-xs text-gray-600 mt-1">Searching...</p>
                  )}
                  
                  {/* Dish Search Results Dropdown */}
                  {searchResults.length > 0 && activeDishSearch && (
                    <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                      {searchResults.map((dishResult, index) => (
                        <div 
                          key={dishResult._id} 
                          className={`p-2 cursor-pointer border-b border-gray-100 ${
                            index === selectedDishIndex ? 'bg-blue-100' : 'hover:bg-gray-100'
                          }`}
                          onClick={() => handleSelectDish(dishResult)}
                          onMouseEnter={() => setSelectedDishIndex(index)}
                        >
                          <span className="font-medium">{dishResult.name}</span>
                          <span className="text-sm text-gray-600 ml-2">({dishResult.unit})</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Quantity *
                  </label>
                  <input
                    type="number"
                    value={editData.quantity}
                    onChange={(e) => setEditData(prev => ({ ...prev, quantity: e.target.value }))}
                    placeholder="Enter quantity"
                    min="0"
                    step="any"
                    onWheel={(e) => e.target.blur()}
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>

                {/* Unit */}
                <div>
                  <label className="block text-gray-700 text-sm font-bold mb-2">
                    Unit
                  </label>
                  <input
                    type="text"
                    value={editData.unit}
                    onChange={(e) => setEditData(prev => ({ ...prev, unit: e.target.value }))}
                    placeholder='Enter Dish Unit'
                    className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                    required
                  />
                </div>
              </div>
            </div>

            {/* Ingredients Section */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4 border-b pb-2">
                Ingredients
              </h3>

              {ingredients.map((ingredient, index) => (
                <div key={ingredient.id} className="mb-4 p-4 border border-gray-300 rounded-lg bg-gray-50">
                  <div className="flex justify-between items-center mb-3">
                    <h4 className="text-md font-semibold text-gray-700">
                      Ingredient {index + 1}
                    </h4>
                    {ingredients.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveIngredient(index)}
                        className="bg-red-500 hover:bg-red-700 text-white font-bold py-1 px-2 rounded text-sm"
                      >
                        Remove
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Ingredient Name Search */}
                    <div className="relative">
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Ingredient Name *
                      </label>
                      <input
                        type="text"
                        value={ingredient.searchTerm}
                        onChange={(e) => handleIngredientSearch(index, e.target.value)}
                        onKeyDown={(e) => handleIngredientKeyDown(e, index)}
                        onFocus={() => setActiveIngredientSearch(prev => ({ ...prev, [index]: true }))}
                        onBlur={() => {
                          // Delay to allow click on dropdown items
                          setTimeout(() => {
                            setActiveIngredientSearch(prev => ({ ...prev, [index]: false }));
                            setSelectedIngredientIndex(prev => ({ ...prev, [index]: -1 }));
                          }, 200);
                        }}
                        placeholder="Search ingredients..."
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                      />
                      {ingredientSearchLoading[index] && (
                        <p className="text-xs text-gray-600 mt-1">Searching...</p>
                      )}
                      
                      {/* Ingredient Search Results Dropdown */}
                      {ingredientSearchResults[index]?.length > 0 && activeIngredientSearch[index] && (
                        <div className="absolute z-20 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-40 overflow-y-auto">
                          {ingredientSearchResults[index].map((ingredientResult, resultIndex) => (
                            <div 
                              key={ingredientResult._id} 
                              className={`p-2 cursor-pointer border-b border-gray-100 ${
                                resultIndex === selectedIngredientIndex[index] ? 'bg-blue-100' : 'hover:bg-gray-100'
                              }`}
                              onClick={() => handleSelectIngredient(index, ingredientResult)}
                              onMouseEnter={() => setSelectedIngredientIndex(prev => ({ ...prev, [index]: resultIndex }))}
                            >
                              <span className="font-medium">{ingredientResult.name}</span>
                              <span className="text-sm text-gray-600 ml-2">({ingredientResult.unit})</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        value={ingredient.quantity}
                        onChange={(e) => handleIngredientQuantityChange(index, e.target.value)}
                        placeholder="Enter quantity"
                        min="0"
                        step="any"
                        onWheel={(e) => e.target.blur()}
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
                        required
                      />
                    </div>

                    {/* Unit */}
                    <div>
                      <label className="block text-gray-700 text-sm font-bold mb-2">
                        Unit
                      </label>
                      <input
                        type="text"
                        value={ingredient.unit}
                        readOnly
                        className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight bg-gray-100"
                        placeholder="Select ingredient first"
                      />
                    </div>
                  </div>
                </div>
              ))}

              {/* Add Ingredient Button */}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleAddIngredient}
                  className="bg-green-500 hover:bg-green-700 text-white font-bold py-2 px-4 rounded"
                >
                  Add Ingredient
                </button>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={handleClose}
                className="bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="bg-lime-800 hover:bg-lime-600 text-white font-bold py-2 px-4 rounded disabled:opacity-50"
              >
                {submitting ? 'Updating...' : 'Update Dish'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditDishModal;